import * as React from 'react'
import Box from '@mui/material/Box'
import { useDropzone } from 'react-dropzone'
import { styled } from '@mui/material/styles'
import DropzoneRejectedContent from 'proposals/components/dropzone/DropzoneRejectedContent'
import DropzoneContent from 'proposals/components/dropzone/DropzoneContent'

const StyledDropzone = styled(Box, {
  shouldForwardProp: (prop) => !['isDragActive', 'isClicking', 'rejected'].includes(prop as string)
})<{
  isDragActive: boolean
  isClicking: boolean  
  rejected: boolean
}>(({ isDragActive, rejected, theme: { spacing, palette } }) => ({
  border: `2px dashed ${isDragActive ? palette.primary.main : palette.grey[300]}`,
  borderRadius: spacing(1),
  padding: spacing(6),
  textAlign: 'center',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  backgroundColor: rejected ? palette.error.light : 'transparent',
  cursor: 'pointer'
}))

type DropzonePropTypes = {
  onDrop: (acceptedFiles: File[]) => void
  disabled: boolean
}

const Dropzone: React.FC<DropzonePropTypes> = ({ onDrop, disabled }) => {
  const [isClicking, setIsClicking] = React.useState<boolean>(false)
  const [rejected, setRejected] = React.useState<boolean>(false)

  const onDropRejected = () => {
    setRejected(true)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: 20971520,
    disabled: disabled
  })

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    if (rejected) {
      timer = setTimeout(() => {
        setRejected(false)
      }, 3000)
    }
    return () => clearTimeout(timer)
  }, [rejected])

  const handleClick = () => {
    setIsClicking(true)

    setTimeout(() => setIsClicking(false), 200)
  }

  return (
    <Box onClick={handleClick}>
      <StyledDropzone isDragActive={isDragActive} isClicking={isClicking} rejected={rejected} {...getRootProps()}>
        <input {...getInputProps()} />
        {rejected ? <DropzoneRejectedContent /> : <DropzoneContent disabled={disabled} />}
      </StyledDropzone>
    </Box>
  )
}

export default Dropzone
